import lodashSnakeCase from 'lodash-es/snakeCase';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

import { ROUTE_NAMES, type RouteName } from '~/constants/routes';

export const eventTypeStandard = { event_type: 'ga4_ce' } as const;

export const pageTypeMap: Record<RouteName, string> = {
    [ROUTE_NAMES.AUCTION_LISTING]: 'alp',
    [ROUTE_NAMES.AUCTION_DETAIL]: 'adp',
    [ROUTE_NAMES.AUCTION_DETAIL_NEGOTIATION]: 'adp_negotiation',
    [ROUTE_NAMES.DIRECT_SALE_LISTING]: 'dslp',
    [ROUTE_NAMES.DIRECT_SALE_DETAIL]: 'dsdp',
    [ROUTE_NAMES.DIRECT_SALE_DETAIL_NEGOTIATION]: 'dsdp_negotiation',
    [ROUTE_NAMES.ACCOUNT_BIDS]: 'account_bids',
    [ROUTE_NAMES.ACCOUNT_PROFILE]: 'account_profile',
    [ROUTE_NAMES.ACCOUNT_VEHICLES_FOR_SALE]: 'account_vehicles_for_sale',
    [ROUTE_NAMES.ACCOUNT_SOLD_VEHICLES]: 'account_sold_vehicles',
    [ROUTE_NAMES.ACCOUNT_BUYER_NEGOTIATIONS]: 'account_buyer_negotiations',
    [ROUTE_NAMES.ACCOUNT_SELLER_NEGOTIATIONS]: 'account_seller_negotiations',
    [ROUTE_NAMES.ACCOUNT_ORDERS_AUCTION]: 'account_orders_auction',
    [ROUTE_NAMES.ACCOUNT_ORDERS]: 'account_orders_direct_sales',
    [ROUTE_NAMES.ACCOUNT_ORDER]: 'account_orders_direct_sales_detail',
    [ROUTE_NAMES.ACCOUNT_ORDER_THANK_YOU]: 'dsdp_thank_you',
    [ROUTE_NAMES.ACCOUNT_ORDER_EDIT]: 'account_orders_direct_sales_detail_edit',
    [ROUTE_NAMES.ACCOUNT_ORDER_EDIT_PAYMENT]: 'account_orders_direct_sales_detail_edit_payment',
    [ROUTE_NAMES.ACCOUNT_FAVORITES]: 'account_favorites',
    [ROUTE_NAMES.ACCOUNT_SALES_REP_SALES]: 'account_sales_rep_sales',
    [ROUTE_NAMES.ACCOUNT_AUCTIONS_VEHICLE_STATS]: 'account_auctions_vehicle_stats',
    [ROUTE_NAMES.ACCOUNT_DIRECT_SALES_VEHICLE_STATS]: 'account_direct_sales_vehicle_stats',
    [ROUTE_NAMES.SMART_LINK_VEHICLE]: 'vehicle_smart_link',
    [ROUTE_NAMES.STORYBLOK_PREVIEW]: 'storyblok_preview',
} as const;

export const getPageType = (routeArg?: RouteLocationNormalizedLoaded): string => {
    const route = routeArg ?? useRoute();
    const routeBaseName = useRouteBaseName();
    const routeName = routeBaseName(route);
    const effectiveRouteName = isCatchAllRouteName(routeName) ? undefined : routeName;
    const { storyblokPageType } = useStoryblokTrackingPageType();

    if (storyblokPageType.value) {
        return storyblokPageType.value;
    }

    return isRouteName(effectiveRouteName) ? pageTypeMap[effectiveRouteName] : lodashSnakeCase(effectiveRouteName?.toString() ?? 'unknown');
};

export const push = (data: Record<string, any>, flush: boolean = true): void => {
    const config = useRuntimeConfig();

    if (config.public.gtmDebugDev && import.meta.dev) {
        // eslint-disable-next-line no-console
        console.log('GTM push:', data);
    }

    if (config.public.googleTagManager.disabled) {
        return;
    }

    const { proxy } = useScriptGoogleTagManager({ scriptInput: { src: config.public.googleTagManager.src, async: true, defer: true } });

    if (!proxy?.dataLayer) return;

    proxy.dataLayer.push(data);

    if (flush) {
        const resetData = Object.keys(data).reduce((obj, key) => ({ ...obj, [key]: null }), {});
        proxy.dataLayer.push(resetData);
    }
};

export const wrapTrackFunctions = <T extends Record<string, Function>>(obj: T): T => {
    const { logError } = useLogs();
    const wrappedFunctions = {} as T;

    for (const [key, fn] of Object.entries(obj)) {
        wrappedFunctions[key as keyof T] = ((...args: any[]): void => {
            try {
                return (fn as Function)(...args);
            } catch (error) {
                logError('Error in GTM track', { trackFn: key, error });
            }
        }) as any;
    }

    return wrappedFunctions;
};
